import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import { SpeakerMuteIcon } from '@components/Icons/SpeakerMuteIcon';
import { VolumeIcon } from '@components/Icons/VolumeIcon';
import { useLowDataMode } from '@hooks/station';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeedItem } from '../../../services/api';
import { formatPublishedTime } from '../../../utils/timeFormatter';

interface ContributorFeedItemProps {
  feed: FeedItem;
  contributorName: string;
  contributorThumbnail: string;
}

export const ContributorFeedItem = memo(
  ({
    feed,
    contributorName,
    contributorThumbnail,
  }: ContributorFeedItemProps) => {
    const { t } = useTranslation();
    const [isLiked, setIsLiked] = useState(false);
    const [likesCount, setLikesCount] = useState(feed.likesCount);
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isLowDataMode] = useLowDataMode();

    // Video state - Twitter/Instagram style
    const [isMuted, setIsMuted] = useState(true);
    const [isPaused, setIsPaused] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    // Caption expansion state
    const [isExpanded, setIsExpanded] = useState(false);
    const CAPTION_CHAR_LIMIT = 200; // Show "See more" after 200 characters

    const username = `@${contributorName.replace(/\s+/g, '').toLowerCase()}`;
    // Convert milliseconds to seconds for formatPublishedTime
    const publishedAtSeconds = Math.floor(feed.publishedAt / 1000);
    const timeString = formatPublishedTime(publishedAtSeconds);

    const handleLikeClick = () => {
      setIsLiked((prev) => !prev);
      setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1));
    };

    // Handle video click to pause/play (Twitter/Instagram style)
    const handleVideoClick = useCallback(() => {
      const video = videoRef.current;
      if (!video) return;

      if (video.paused) {
        video.play();
        setIsPaused(false);
      } else {
        video.pause();
        setIsPaused(true);
      }
    }, []);

    // Handle mute toggle (separate from pause/play)
    const handleMuteToggle = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        const video = videoRef.current;
        if (!video) return;

        const newMutedState = !isMuted;
        video.muted = newMutedState;
        setIsMuted(newMutedState);
      },
      [isMuted],
    );

    // Handle progress bar update
    const handleTimeUpdate = useCallback(() => {
      const video = videoRef.current;
      if (!video || isDragging) return;
      const progressPercent = (video.currentTime / video.duration) * 100;
      setProgress(progressPercent);
    }, [isDragging]);

    // Handle seeking
    const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      const video = videoRef.current;
      if (!video) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const newTime = (percentage / 100) * video.duration;

      video.currentTime = newTime;
      setProgress(percentage);
    }, []);

    // Auto-play muted on mount (Twitter/Instagram style)
    useEffect(() => {
      const video = videoRef.current;
      if (!video || isLowDataMode || feed.mediaType !== 'video') return;

      video.muted = true;
      video.loop = true;
      video.play().catch(() => {
        // Autoplay failed - possibly due to browser restrictions
      });
    }, [isLowDataMode, feed.mediaType]);

    const bgStyles = [
      'bg-[linear-gradient(180deg,rgba(97,244,255,0.2)_0%,rgba(97,244,255,0)_100%)]',
      'bg-[linear-gradient(180deg,rgba(255,222,120,0.2)_0%,rgba(255,222,120,0)_100%)]',
      'bg-[linear-gradient(180deg,rgba(255,120,180,0.2)_0%,rgba(255,120,180,0)_100%)]',
      'bg-[linear-gradient(180deg,rgba(120,255,120,0.2)_0%,rgba(120,255,120,0)_100%)]',
    ];
    const bgStyle = bgStyles[Math.floor(Math.random() * bgStyles.length)];

    return (
      <div
        className={`w-full ${bgStyle} rounded-[16px] pb-[16px] font-[NapsterSans] mb-4`}>
        {/* Header with contributor info */}
        <div className="flex p-4 w-full gap-2 items-center">
          <div className="flex w-[36px] h-[36px] rounded-full overflow-hidden">
            <img
              className="w-full h-full object-cover"
              src={contributorThumbnail || '/assets/images/logged_user.png'}
              alt={contributorName}
            />
          </div>
          <div className="flex flex-col flex-1">
            <div className="flex text-[14px] font-semibold leading-[130%] text-left text-white pb-[2px] font-[NapsterSans]">
              {contributorName}
            </div>
            <div className="flex gap-[5px] items-center font-[NapsterSans]">
              <div className="flex text-[12px] font-normal leading-[130%] text-left text-white/60 pb-[2px]">
                {username}
              </div>
              <div className="flex text-[12px] font-normal leading-[130%] text-left text-white/60 font-[NapsterSans]">
                • {timeString}
              </div>
            </div>
          </div>
        </div>

        {/* Caption */}
        <div className="w-full px-4 pt-[0] pb-3 text-[14px] font-normal leading-[150%] text-left text-white">
          <div className="whitespace-pre-line">
            {feed.caption &&
            feed.caption.length > CAPTION_CHAR_LIMIT &&
            !isExpanded
              ? feed.caption.substring(0, CAPTION_CHAR_LIMIT) + '...'
              : feed.caption}
          </div>
          {feed.caption && feed.caption.length > CAPTION_CHAR_LIMIT && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-white/60 hover:text-white text-[14px] font-medium mt-1 transition-colors">
              {isExpanded ? t('contributor.seeLess') : t('contributor.seeMore')}
            </button>
          )}
        </div>

        {/* Media content */}
        <div className="w-full px-4 pb-4">
          <div className="w-full rounded-[8px] overflow-hidden relative">
            {feed.mediaType === 'video' ? (
              isLowDataMode ? (
                <img
                  className="w-full h-auto object-cover"
                  src={feed.thumbnailUrl || feed.mediaUrl}
                  alt={feed.caption}
                />
              ) : (
                <div className="relative" onClick={handleVideoClick}>
                  <video
                    ref={videoRef}
                    className="w-full h-auto object-cover cursor-pointer"
                    src={feed.mediaUrl}
                    poster={feed.thumbnailUrl || undefined}
                    playsInline
                    preload="metadata"
                    muted
                    loop
                    onTimeUpdate={handleTimeUpdate}
                  />

                  {/* Progress bar - shown when paused (Twitter/Instagram style) */}
                  {isPaused && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSeek(e);
                      }}>
                      <div
                        className="h-full bg-white transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  )}

                  {/* Mute/Unmute icon - bottom right corner (Twitter/Instagram style) */}
                  <button
                    onClick={handleMuteToggle}
                    className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors text-white z-10">
                    {isMuted ? <SpeakerMuteIcon /> : <VolumeIcon />}
                  </button>
                </div>
              )
            ) : (
              <img
                className="w-full h-auto object-cover"
                src={feed.mediaUrl}
                alt={feed.caption}
              />
            )}
          </div>

          {/* Like button */}
          <div className="flex w-full gap-[4px] relative z-[1] mt-3">
            <button
              className="flex items-center gap-[4px] h-[28px] border-1 border-white/20 w-fit px-3 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              onClick={handleLikeClick}>
              {isLiked ? (
                <Likedicon width={16} height={16} />
              ) : (
                <FavoriteIcon width={16} height={16} fillOpacity={0.8} />
              )}
              {likesCount > 0 && (
                <span className="text-[10px] font-normal leading-[140%] text-white text-left">
                  {likesCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  },
);

ContributorFeedItem.displayName = 'ContributorFeedItem';
